// LabReport.js
//
// Public page for https://healthcarelife.in/lab-report/:id
// (mount this at a route like <Route path="/lab-report/:id" element={<LabReport />} />)
//
// Flow:
//   1. On mount, silently fetch BookingReportStatusDTO via LabReportService
//      (GET /makereportfirsttype/booking/{id}/report-status).
//   2. Nothing from that response is shown yet — a modal asks for the
//      patient's mobile number first.
//   3. On submit, the entered number is compared against
//      data.patientDTO.phone (which the backend already populates from
//      PatientCompanyDTO.getPatientMobile()). Only on a match do we reveal
//      the page.
//   4. Once verified:
//        - paymentDue === true  -> reports are withheld (backend already
//          returns an empty reports[] in this case) and we show a payment
//          notice instead.
//        - paymentDue === false && allReportsCompleted === false -> tests
//          are shown but the Print button just raises a notice instead of
//          printing.
//        - paymentDue === false && allReportsCompleted === true -> Print
//          builds one merged, multi-page print job via
//          HandleLabReportPrint (one page per test, printed once).

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { serverConfig } from "../ApiSearchPath/ApiSearchPath";
import LabReportService from "./Service/LabReportService"; // move into ./Service/ to match your MakeReportService.js convention if preferred
import { buildPrintDataList } from "./LabReportPrintData";
import HandleLabReportPrint from "./HandleLabReportPrint";

// Compare only the last 10 digits so it doesn't matter whether either side
// carries a country code, spaces, or dashes.
const normalizeMobile = (value) => (value || "").replace(/\D/g, "").slice(-10);

export default function LabReport() {
  // Query param instead of a /:id path segment — keeps the route flat
  // (/lab-report?id=1) so relative asset paths (assets/img/... used in the
  // breadcrumb below) still resolve against the same base path as every
  // other page, instead of resolving relative to /lab-report/1/.
  const [searchParams] = useSearchParams();
  const patientTestBookingId = searchParams.get("id");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState(null);

  const [verified, setVerified] = useState(false);
  const [mobileInput, setMobileInput] = useState("");
  const [verifying, setVerifying] = useState(false);

  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!patientTestBookingId) {
        setLoading(false);
        setLoadError(true);
        return;
      }
      setLoading(true);
      try {
        const response = await LabReportService.fetchBookingReportStatus(patientTestBookingId);
        if (cancelled) return;
        setData(response?.data?.data || null);
      } catch (error) {
        console.error("Failed to load lab report:", error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [patientTestBookingId]);

  const handleVerifyMobile = async (e) => {
    e.preventDefault();
    const entered = normalizeMobile(mobileInput);
    const onFile = normalizeMobile(data?.patientDTO?.phone);

    if (!entered || entered.length < 10) {
      Swal.fire({
        title: "Invalid Number",
        text: "Please enter a valid 10-digit mobile number.",
        icon: "warning",
        confirmButtonColor: "#566fe2",
      });
      return;
    }

    setVerifying(true);
    // Small delay purely so the click doesn't feel instantaneous/guessable —
    // remove if you'd rather it resolve immediately.
    setTimeout(() => {
      setVerifying(false);
      if (onFile && entered === onFile) {
        setVerified(true);
      } else {
        Swal.fire({
          title: "Not Verified",
          text: "The mobile number you entered doesn't match our records for this report.",
          icon: "error",
          confirmButtonColor: "#566fe2",
        });
      }
    }, 300);
  };

  const handlePrintAll = async () => {
    if (data?.paymentDue) return; // guarded by disabled button too
    if (!data?.allReportsCompleted) {
      Swal.fire({
        title: "Not Ready",
        text: "Report generation is not completed yet for one or more tests. Please check back later.",
        icon: "info",
        confirmButtonColor: "#566fe2",
      });
      return;
    }

    setIsPrinting(true);
    try {
      // buildPrintDataList is async — it resolves each test's datasource
      // lookups (collectDatasources -> DATASOURCE_FETCHERS) before the
      // print window is opened.
      const printDataList = await buildPrintDataList(data.reports);
      HandleLabReportPrint(printDataList, data.patientDTO, data.bookingDTO);
    } catch (error) {
      console.error("Print failed:", error);
      Swal.fire({
        title: "Print Failed",
        text: "Something went wrong while preparing the report for print.",
        icon: "error",
        confirmButtonColor: "#566fe2",
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const patient = data?.patientDTO;
  const booking = data?.bookingDTO;

  return (
    <>
      <style>{`
        .lab-report-modal-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.55);
          display: flex; align-items: center; justify-content: center;
          z-index: 1050;
        }
        .lab-report-modal-card {
          background: #fff; border-radius: 12px; padding: 28px;
          width: 92%; max-width: 380px; text-align: center;
          box-shadow: 0 10px 40px rgba(0,0,0,0.25);
        }
        .lab-report-verified-badge {
          color: #198754; font-weight: 600; font-size: 13px;
        }
        .lab-report-print-btn:disabled {
          opacity: 0.55; cursor: not-allowed;
        }
      `}</style>

      {/* ── Breadcrumb (mirrors CompanyProfile.js's pattern) ── */}
      <div className="breadcrumb-bar">
        <div className="container">
          <div className="row align-items-center inner-banner">
            <div className="col-md-12 col-12 text-center">
              <nav aria-label="breadcrumb" className="page-breadcrumb">
                <h2 className="breadcrumb-title">Lab Report</h2>
              </nav>
            </div>
          </div>
        </div>
        <div className="breadcrumb-bg">
          <img src="assets/img/bg/breadcrumb-bg-01.png" alt="img" className="breadcrumb-bg-01" />
          <img src="assets/img/bg/breadcrumb-bg-02.png" alt="img" className="breadcrumb-bg-02" />
        </div>
      </div>

      <div className="content">
        <div className="container">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
              <div className="text-center">
                <i className="fa-solid fa-spinner fa-spin fa-4x mb-4" style={{ color: "#0d6efd" }} />
                <h4 className="mb-2">Loading your report...</h4>
              </div>
            </div>
          ) : loadError || !data ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "30vh" }}>
              <div className="text-center">
                <h2 className="mb-2" style={{ color: "#ed4e2b" }}>Report not found</h2>
                <p className="text-muted">Please check the link and try again.</p>
              </div>
            </div>
          ) : !verified ? (
            <>
              {/* ── Mobile verification modal ── */}
              <div className="lab-report-modal-backdrop">
                <form className="lab-report-modal-card" onSubmit={handleVerifyMobile}>
                  <i className="fa-solid fa-lock fa-2x mb-3" style={{ color: "#566fe2" }} />
                  <h4 className="mb-2">Verify Your Identity</h4>
                  <p className="text-muted mb-3">
                    Enter the mobile number used for this booking to view your report.
                  </p>
                  <input
                    type="tel"
                    inputMode="numeric"
                    className="form-control text-center mb-3"
                    placeholder="Enter mobile number"
                    value={mobileInput}
                    onChange={(e) => setMobileInput(e.target.value)}
                    maxLength={15}
                    autoFocus
                  />
                  <button type="submit" className="btn btn-primary w-100" disabled={verifying}>
                    {verifying ? "Verifying..." : "View Report"}
                  </button>
                </form>
              </div>
              {/* Background stays blank/blurred while unverified */}
              <div style={{ minHeight: "50vh" }} />
            </>
          ) : (
            <div>
              {/* ── Patient / company banner ── */}
              <div className="card doc-profile-card mb-4">
                <div className="card-body">
                  {patient?.headerImg && (
                    <img
                      src={`${serverConfig.BASE_URL}/uploads/company/${patient.headerImg}`}
                      alt="Lab"
                      className="img-fluid mb-3"
                      style={{ maxHeight: 90, objectFit: "contain" }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className="row">
                    <div className="col-md-4"><strong>Patient Name:</strong> {patient?.patientName || "—"}</div>
                    <div className="col-md-4"><strong>Patient Id:</strong> {patient?.patientNumber || "—"}</div>
                    <div className="col-md-4">
                      <strong>Age / Sex:</strong> {patient?.age ? `${patient.age} yrs` : "—"} / {patient?.gender || "—"}
                    </div>
                    <div className="col-md-4 mt-2"><strong>Booking Id:</strong> {booking?.bookingId || "—"}</div>
                    <div className="col-md-4 mt-2"><strong>Lab / Company:</strong> {patient?.companyName || "—"}</div>
                    <div className="col-md-4 mt-2">
                      <span className="lab-report-verified-badge"><i className="fa-solid fa-circle-check me-1" />Identity verified</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Status-gated content ── */}
              {data.paymentDue ? (
                <div className="card">
                  <div className="card-body text-center py-5">
                    <i className="fa-solid fa-circle-exclamation fa-3x mb-3" style={{ color: "#ed4e2b" }} />
                    <h4 className="mb-2" style={{ color: "#ed4e2b" }}>Payment Pending</h4>
                    <p className="text-muted mb-0">
                      Your report can't be shown until payment for this booking is completed.
                      Please contact the lab to complete your payment.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">
                        Tests
                        {!data.allReportsCompleted && (
                          <span className="badge bg-warning text-dark ms-2">Processing</span>
                        )}
                      </h5>
                      <button
                        type="button"
                        className="btn btn-primary lab-report-print-btn"
                        onClick={handlePrintAll}
                        disabled={isPrinting}
                      >
                        <i className="fa-solid fa-print me-1" />
                        {isPrinting ? "Preparing..." : "Print Report"}
                      </button>
                    </div>

                    {!data.allReportsCompleted ? (
                      <p className="text-muted mb-0">
                        Your report is still being processed. Please check back later — you'll be able to
                        view and print it here once it's completed.
                      </p>
                    ) : (
                      <ul className="list-group">
                        {(data.reports || []).map((r) => (
                          <li key={r.makeReportId} className="list-group-item d-flex justify-content-between align-items-center">
                            {r.mainTestName || r.testHeader}
                            <span className="badge bg-success">Completed</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}